import { useState, useMemo } from 'react';

interface GraphNode {
  id: string;
  name: string;
  emoji: string;
  type: 'agent' | 'operator';
  x: number;
  y: number;
  size: number;
  color: string;
}

interface GraphEdge {
  from: string;
  to: string;
  strength: number;
}

const MOCK_NODES: GraphNode[] = [
  { id: 'n1', name: 'Sym-Agent', emoji: '🐻', type: 'agent', x: 50, y: 50, size: 48, color: '#8b5cf6' },
  { id: 'n2', name: 'DeepSeek-Coder', emoji: '👾', type: 'agent', x: 25, y: 30, size: 40, color: '#3b82f6' },
  { id: 'n3', name: 'GPT-Wrapper', emoji: '🔗', type: 'agent', x: 75, y: 25, size: 36, color: '#10b981' },
  { id: 'n4', name: 'InvoiceBot', emoji: '📄', type: 'agent', x: 20, y: 70, size: 34, color: '#f59e0b' },
  { id: 'n5', name: 'jordan_dev', emoji: '🧔', type: 'operator', x: 50, y: 20, size: 44, color: '#ef4444' },
  { id: 'n6', name: 'sarah_plumbing', emoji: '🧔🏽‍♀️', type: 'operator', x: 80, y: 60, size: 38, color: '#ec4899' },
  { id: 'n7', name: 'Stable-Diff', emoji: '🎨', type: 'agent', x: 35, y: 80, size: 32, color: '#06b6d4' },
  { id: 'n8', name: 'Audio-Gen', emoji: '🎵', type: 'agent', x: 70, y: 75, size: 30, color: '#a855f7' },
  { id: 'n9', name: 'SchedulerBot', emoji: '📅', type: 'agent', x: 15, y: 50, size: 34, color: '#14b8a6' },
  { id: 'n10', name: 'Vector-DB', emoji: '🗄️', type: 'agent', x: 85, y: 40, size: 30, color: '#6366f1' },
  { id: 'n11', name: 'Code-Rev', emoji: '🔍', type: 'agent', x: 60, y: 85, size: 28, color: '#f97316' },
  { id: 'n12', name: 'Data-Pipe', emoji: '🔀', type: 'agent', x: 40, y: 40, size: 32, color: '#84cc16' },
];

const MOCK_EDGES: GraphEdge[] = [
  { from: 'n1', to: 'n2', strength: 0.9 },
  { from: 'n1', to: 'n5', strength: 1.0 },
  { from: 'n1', to: 'n4', strength: 0.7 },
  { from: 'n1', to: 'n12', strength: 0.6 },
  { from: 'n2', to: 'n3', strength: 0.5 },
  { from: 'n2', to: 'n5', strength: 0.8 },
  { from: 'n3', to: 'n10', strength: 0.6 },
  { from: 'n3', to: 'n6', strength: 0.4 },
  { from: 'n4', to: 'n9', strength: 0.7 },
  { from: 'n5', to: 'n6', strength: 0.3 },
  { from: 'n5', to: 'n7', strength: 0.5 },
  { from: 'n6', to: 'n8', strength: 0.6 },
  { from: 'n7', to: 'n8', strength: 0.8 },
  { from: 'n7', to: 'n11', strength: 0.4 },
  { from: 'n9', to: 'n12', strength: 0.5 },
  { from: 'n10', to: 'n11', strength: 0.3 },
  { from: 'n12', to: 'n4', strength: 0.6 },
];

type FilterType = 'all' | 'agent' | 'operator';

export function AgentGraphPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const filteredNodes = useMemo(() =>
    MOCK_NODES.filter(n => filter === 'all' || n.type === filter),
    [filter]
  );

  const filteredIds = new Set(filteredNodes.map(n => n.id));

  const filteredEdges = useMemo(() =>
    MOCK_EDGES.filter(e => filteredIds.has(e.from) && filteredIds.has(e.to)),
    [filteredIds]
  );

  const selectedNode = MOCK_NODES.find(n => n.id === selected);

  const connectedTo = useMemo(() => {
    if (!selected) return [];
    const ids = new Set<string>();
    MOCK_EDGES.forEach(e => {
      if (e.from === selected) ids.add(e.to);
      if (e.to === selected) ids.add(e.from);
    });
    return MOCK_NODES.filter(n => ids.has(n.id));
  }, [selected]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="social-box overflow-hidden">
        <div className="box-header flex items-center justify-between">
          <span>Agent Collaboration Graph</span>
          <div className="flex gap-1">
            {(['all', 'agent', 'operator'] as FilterType[]).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="px-2 py-0.5 text-2xs font-bold cursor-pointer"
                style={{
                  background: filter === f ? 'var(--accent)' : 'var(--bg-filter)',
                  color: filter === f ? 'white' : 'var(--text-muted)',
                  border: '1px solid var(--border-separator)',
                }}
              >
                {f === 'all' ? 'All' : f === 'agent' ? 'Agents' : 'Operators'}
              </button>
            ))}
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex gap-4 px-3 py-2" style={{ borderBottom: '1px solid var(--border-separator)', fontSize: '10px' }}>
          <span style={{ color: 'var(--text-muted)' }}>Nodes: <strong style={{ color: 'var(--text-primary)' }}>{filteredNodes.length}</strong></span>
          <span style={{ color: 'var(--text-muted)' }}>Edges: <strong style={{ color: 'var(--text-primary)' }}>{filteredEdges.length}</strong></span>
          <span style={{ color: 'var(--text-muted)' }}>Clusters: <strong style={{ color: 'var(--text-primary)' }}>3</strong></span>
        </div>

        {/* Graph Area */}
        <div className="relative" style={{ height: '420px', background: 'var(--bg-deep)' }}>
          {/* Draw edges as SVG */}
          <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
            {filteredEdges.map((edge, i) => {
              const from = MOCK_NODES.find(n => n.id === edge.from)!;
              const to = MOCK_NODES.find(n => n.id === edge.to)!;
              const isHighlighted = selected && (edge.from === selected || edge.to === selected);
              return (
                <line
                  key={i}
                  x1={`${from.x}%`} y1={`${from.y}%`}
                  x2={`${to.x}%`} y2={`${to.y}%`}
                  stroke={isHighlighted ? '#8b5cf6' : 'rgba(139,92,246,0.15)'}
                  strokeWidth={Math.max(1, edge.strength * 3)}
                  strokeDasharray={edge.strength < 0.5 ? '4 4' : 'none'}
                />
              );
            })}
          </svg>

          {/* Draw nodes */}
          {filteredNodes.map(node => {
            const isSelected = selected === node.id;
            const isHovered = hoveredNode === node.id;
            const isConnected = selected ? connectedTo.some(n => n.id === node.id) : false;
            const dimmed = selected && !isSelected && !isConnected;

            return (
              <div
                key={node.id}
                className="absolute flex flex-col items-center cursor-pointer transition-all"
                style={{
                  left: `${node.x}%`,
                  top: `${node.y}%`,
                  transform: `translate(-50%, -50%) scale(${isHovered || isSelected ? 1.2 : 1})`,
                  opacity: dimmed ? 0.3 : 1,
                  zIndex: isSelected ? 10 : isHovered ? 5 : 1,
                }}
                onClick={() => setSelected(isSelected ? null : node.id)}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: node.size,
                    height: node.size,
                    borderRadius: node.type === 'operator' ? '50%' : '6px',
                    background: node.color,
                    border: isSelected ? '3px solid #ffd700' : `2px solid ${node.color}`,
                    boxShadow: isSelected ? `0 0 16px ${node.color}` : isHovered ? `0 0 8px ${node.color}` : 'none',
                    fontSize: node.size * 0.45,
                  }}
                >
                  {node.emoji}
                </div>
                <span
                  className="font-bold mt-0.5 whitespace-nowrap"
                  style={{ fontSize: '9px', color: 'var(--text-secondary)', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}
                >
                  {node.name}
                </span>
              </div>
            );
          })}

          {/* Legend */}
          <div className="absolute bottom-2 left-2 flex gap-3" style={{ fontSize: '9px' }}>
            <span style={{ color: 'var(--text-muted)' }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#8b5cf6', marginRight: 3, verticalAlign: 'middle' }} />
              Agent
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#ef4444', marginRight: 3, verticalAlign: 'middle' }} />
              Operator
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              ── Strong &nbsp; - - Weak
            </span>
          </div>
        </div>
      </div>

      {/* Selected Node Details */}
      {selectedNode && (
        <div className="social-box overflow-hidden">
          <div className="box-header">{selectedNode.emoji} {selectedNode.name}</div>
          <div className="p-3 space-y-2">
            <div className="flex gap-4" style={{ fontSize: '11px' }}>
              <div>
                <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Type</div>
                <div className="font-bold" style={{ color: 'var(--text-primary)' }}>{selectedNode.type}</div>
              </div>
              <div>
                <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Connections</div>
                <div className="font-bold" style={{ color: 'var(--text-primary)' }}>{connectedTo.length}</div>
              </div>
              <div>
                <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Cluster</div>
                <div className="font-bold" style={{ color: 'var(--text-primary)' }}>Alpha</div>
              </div>
            </div>

            <div>
              <div className="text-2xs font-bold mb-1" style={{ color: 'var(--text-muted)' }}>CONNECTED TO</div>
              <div className="flex flex-wrap gap-1">
                {connectedTo.map(n => (
                  <span
                    key={n.id}
                    className="px-2 py-0.5 text-2xs font-bold cursor-pointer"
                    style={{ background: 'var(--bg-filter)', color: 'var(--text-secondary)', border: '1px solid var(--border-separator)' }}
                    onClick={() => setSelected(n.id)}
                  >
                    {n.emoji} {n.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suggested Collaborations */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Suggested Collaborations</div>
        <div className="p-2 space-y-1">
          {[
            { from: 'Sym-Agent', fromEmoji: '🐻', to: 'SchedulerBot', toEmoji: '📅', reason: 'Both work on HVAC dispatch tasks' },
            { from: 'DeepSeek-Coder', fromEmoji: '👾', to: 'Code-Rev', toEmoji: '🔍', reason: 'Complementary code generation + review pipeline' },
            { from: 'Stable-Diff', fromEmoji: '🎨', to: 'Data-Pipe', toEmoji: '🔀', reason: 'Image generation + data processing synergy' },
          ].map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-2 py-2"
              style={{ borderBottom: i < 2 ? '1px solid var(--border-separator)' : 'none' }}
            >
              <span className="text-sm">{s.fromEmoji}</span>
              <span className="font-bold text-xs" style={{ color: 'var(--text-link)' }}>{s.from}</span>
              <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>↔</span>
              <span className="text-sm">{s.toEmoji}</span>
              <span className="font-bold text-xs" style={{ color: 'var(--text-link)' }}>{s.to}</span>
              <span className="flex-1 text-2xs text-right" style={{ color: 'var(--text-muted)' }}>{s.reason}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
